import { bool, func, node, number, object, shape, string } from "prop-types";
import { useLayoutEffect, useState } from "react";

import styled from "styled-components/macro";

import { UNIT_PLACEHOLDER } from "Utilities/Address/AddressValidation/providers/smarty/smartyAutocomplete";

import ShadowText from "./ShadowText";

const preventDefaultMouseDown = (e) => e.preventDefault();

const HOTSPOT_FALLBACK_STYLE = { top: 0, bottom: 0, right: 0, width: "50%" };

// One shared 2d canvas context, lazily created and reused across every instance only to
// measure ghost-text width (it paints nothing). Null-tolerant: jsdom has no canvas, so
// geometry stays unmeasured and the hotspot falls back to the input's right half — test
// clicks hit `aria-label`, not pixels.
let measureCtx;
function getMeasureContext() {
  if (measureCtx !== undefined) return measureCtx;
  try {
    measureCtx = document.createElement("canvas").getContext("2d") || null;
  } catch {
    measureCtx = null;
  }
  return measureCtx;
}

/**
 * Wraps a text input with the position-relative anchor + `ShadowText` overlay the
 * autocomplete ghost text needs, so each address field doesn't repeat that boilerplate.
 *
 * `inputRef` must be the SAME ref attached to the wrapped input — `ShadowText` and the
 * unit hotspot measure the input's geometry through it.
 *
 * `unitFill` opts the field into the multi-unit fill affordance — the invisible
 * `UnitHotspot` laid over the "___" sentinel (only the street field passes it).
 * `acceptRightOffset` insets the hotspot's right edge from the input's right edge so
 * surfaces that pin a country flag there can seat it left of the flag.
 */
export default function ShadowField({
  inputRef,
  typed,
  suffix,
  unitFill,
  acceptRightOffset = 4,
  children,
}) {
  const showUnitFill = Boolean(unitFill?.show);
  // Geometry for the invisible hotspot laid over the painted "___" sentinel. Measured in
  // the input's own font and against the same offsetParent ShadowText uses, so the box
  // lines up with the rendered ghost. Null until measured (and in jsdom).
  const [hotspot, setHotspot] = useState(null);
  useLayoutEffect(() => {
    const input = inputRef && inputRef.current;
    const ctx = getMeasureContext();
    const sentinelIdx = (suffix || "").lastIndexOf(UNIT_PLACEHOLDER);
    if (!input || !showUnitFill || !ctx || sentinelIdx < 0) {
      setHotspot(null);
      return;
    }
    const parent = input.offsetParent || input.parentElement;
    if (!parent) {
      setHotspot(null);
      return;
    }
    const cs = window.getComputedStyle(input);
    const inputRect = input.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    const parentStyle = window.getComputedStyle(parent);
    const parentBorderTop = parseFloat(parentStyle.borderTopWidth) || 0;
    const parentBorderLeft = parseFloat(parentStyle.borderLeftWidth) || 0;
    const inputLeft = inputRect.left - parentRect.left - parentBorderLeft;
    const inputTop = inputRect.top - parentRect.top - parentBorderTop;
    // Start of the input's text content (border + padding) — matches ShadowText's left inset.
    const contentStart =
      inputLeft +
      (parseFloat(cs.borderLeftWidth) || 0) +
      (parseFloat(cs.paddingLeft) || 0);
    ctx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    // The input renders typed immediately followed by suffix, so the placeholder's x is the
    // measured width of everything before it: typed plus the suffix slice up to the sentinel.
    const beforeSentinel = (typed || "") + (suffix || "").slice(0, sentinelIdx);
    const left = contentStart + ctx.measureText(beforeSentinel).width;
    const rightEdge = inputLeft + inputRect.width - acceptRightOffset;
    const next = {
      top: inputTop,
      left,
      width: Math.max(0, rightEdge - left),
      height: inputRect.height,
    };
    // Keep the previous object when nothing changed so the reference stays stable and the
    // hotspot's style prop doesn't churn on each remeasure.
    setHotspot((prev) =>
      prev &&
      prev.top === next.top &&
      prev.left === next.left &&
      prev.width === next.width &&
      prev.height === next.height
        ? prev
        : next
    );
  }, [inputRef, showUnitFill, typed, suffix, acceptRightOffset]);
  return (
    <Wrapper>
      <ShadowText anchorRef={inputRef} typed={typed} suffix={suffix} />
      {children}
      {showUnitFill ? (
        <UnitHotspot
          aria-label="fill-unit"
          type="button"
          // Pointer-only: keyboard users fill the unit via Tab/Enter on the ghost, so a tab
          // stop here would be redundant; onMouseDown keeps the click from blurring the input.
          tabIndex={-1}
          onMouseDown={preventDefaultMouseDown}
          onClick={unitFill.onFill}
          style={hotspot || HOTSPOT_FALLBACK_STYLE}
        />
      ) : null}
    </Wrapper>
  );
}

ShadowField.propTypes = {
  inputRef: object.isRequired,
  typed: string,
  suffix: string,
  unitFill: shape({ show: bool, onFill: func }),
  acceptRightOffset: number,
  children: node,
};

const Wrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

// Transparent click target over the "___" sentinel, sized inline from the measured
// `hotspot` geometry. z-index 2 sits it above the ghost overlay (z-index 1) so it gets the click.
const UnitHotspot = styled.button`
  position: absolute;
  z-index: 2;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  appearance: none;
  cursor: pointer;
`;
